import React, { useEffect, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { analytics } from '../../services/analytics';

interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  role: string;
}

interface NavItem {
  label: string;
  path: string;
  icon: string;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: '📊' },
  { label: 'Agents', path: '/agents', icon: '🤖' },
  { label: 'Phones', path: '/phones', icon: '📞' },
  { label: 'Live Monitor', path: '/monitor', icon: '🎛️' },
  { label: 'AI Planner', path: '/planner', icon: '🧠' },
  { label: 'Research', path: '/research', icon: '🧪' },
  { label: 'Campaigns', path: '/campaigns', icon: '📢' },
  { label: 'A/B Tests', path: '/experiments', icon: '🧪' },
  { label: 'Compliance', path: '/compliance', icon: '🛡️' },
  { label: 'Logs', path: '/call-logs', icon: '📋' },
  { label: 'Analytics', path: '/analytics', icon: '📊' },
  { label: 'Billing', path: '/billing', icon: '💳' },
  { label: 'Integrations', path: '/integrations', icon: '🔌' },
  { label: 'Webhooks', path: '/settings/webhooks', icon: '🔗' },
  { label: 'Settings', path: '/settings', icon: '⚙️' },
];

const API = process.env.REACT_APP_API_URL || 'http://localhost:3002';

export const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [, setSidebarOpen] = useState(false);
  const [activeCalls, setActiveCalls] = useState(0);
  const monitorPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    axios.get(`${API}/api/v1/auth/me`, { withCredentials: true })
      .then(res => {
        const u = res.data.data;
        setUser(u);
        if (u?.id) {
          analytics.identify(u.id, { email: u.email, name: u.name, plan: u.plan_type, createdAt: u.created_at });
        }
      })
      .catch(() => {});
  }, []);

  // Poll live stats for active call badge
  useEffect(() => {
    const fetchStats = () => {
      axios.get(`${API}/api/v1/monitor/stats/live`, { withCredentials: true })
        .then(res => setActiveCalls(res.data?.data?.activeCalls ?? 0))
        .catch(() => {});
    };
    fetchStats();
    monitorPollRef.current = setInterval(fetchStats, 5000);
    return () => {
      if (monitorPollRef.current) clearInterval(monitorPollRef.current);
    };
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    try { await axios.post(`${API}/api/v1/auth/logout`, {}, { withCredentials: true }); } catch {}
    analytics.reset();
    window.location.href = '/login';
  };

  const isActive = (path: string) =>
    path === '/dashboard' ? location.pathname === '/dashboard' : location.pathname.startsWith(path);

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 py-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center text-lg flex-shrink-0"
          style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.25)' }}
        >
          📞
        </div>
        <span className="text-white font-semibold text-base">CallPilot</span>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {navItems.map((item) => {
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 text-sm font-medium transition-all duration-150 no-underline"
              style={{
                color: active ? '#ffffff' : '#9ca3af',
                background: active ? 'rgba(99,102,241,0.15)' : 'transparent',
                border: active ? '1px solid rgba(99,102,241,0.2)' : '1px solid transparent',
              }}
            >
              <span className="text-base w-5 text-center flex-shrink-0">{item.icon}</span>
              <span className="flex-1">{item.label}</span>
              {item.path === '/monitor' && activeCalls > 0 && (
                <span
                  className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{ background: '#ef4444', color: '#fff', minWidth: 18, textAlign: 'center' }}
                >
                  {activeCalls}
                </span>
              )}
              {item.path === '/compliance' && (
                <span
                  className="flex-shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{ background: 'rgba(220,38,38,0.2)', color: '#ef4444', border: '1px solid rgba(220,38,38,0.3)' }}
                >
                  REQ
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* User + Logout */}
      <div className="px-3 py-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        {user && (
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            {user.avatar ? (
              <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full flex-shrink-0 object-cover" />
            ) : (
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0"
                style={{ background: 'rgba(99,102,241,0.25)', color: '#818cf8' }}
              >
                {user.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-white truncate">{user.name}</div>
              <div className="text-xs text-gray-500 truncate">{user.email}</div>
            </div>
          </div>
        )}
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150"
          style={{ color: '#9ca3af' }}
        >
          <span className="text-base w-5 text-center">🚪</span>
          <span>Logout</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen" style={{ background: '#0a0a0f' }}>

      {/* ── Desktop sidebar (md+) ─────────────────────────────────────── */}
      <aside
        className="hidden md:flex flex-col flex-shrink-0"
        style={{
          width: 240,
          background: '#0d0d15',
          borderRight: '1px solid rgba(255,255,255,0.06)',
          position: 'fixed',
          top: 0, left: 0, bottom: 0,
          zIndex: 40,
        }}
      >
        <SidebarContent />
      </aside>

      {/* ── Mobile top bar ────────────────────────────────────────────── */}
      <header
        className="md:hidden fixed top-0 left-0 right-0 flex items-center justify-between px-4 py-3 z-50"
        style={{ background: '#0d0d15', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center text-base flex-shrink-0"
            style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.25)' }}
          >
            📞
          </div>
          <span className="text-white font-semibold text-sm">CallPilot</span>
        </div>
        {user && (
          <div className="flex items-center gap-2">
            {user.avatar ? (
              <img src={user.avatar} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
            ) : (
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold"
                style={{ background: 'rgba(99,102,241,0.25)', color: '#818cf8' }}
              >
                {user.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
          </div>
        )}
      </header>

      {/* ── Main content ──────────────────────────────────────────────── */}
      <main
        className="flex-1 min-w-0"
        style={{
          marginLeft: 0,
          paddingTop: 0,
        }}
      >
        {/* Desktop: push right of sidebar */}
        <div className="hidden md:block" style={{ marginLeft: 240, minHeight: '100vh' }}>
          {children}
        </div>
        {/* Mobile: full width, top bar + bottom nav padding */}
        <div className="md:hidden" style={{ paddingTop: 56, paddingBottom: 64, minHeight: '100vh' }}>
          {children}
        </div>
      </main>

      {/* ── Mobile bottom nav ─────────────────────────────────────────── */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 flex items-center justify-around z-50 px-1 py-1"
        style={{
          background: '#0d0d15',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          height: 60,
        }}
      >
        {navItems.map((item) => {
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className="flex flex-col items-center justify-center gap-0.5 flex-1 py-1 rounded-lg transition-all duration-150 no-underline"
              style={{
                color: active ? '#818cf8' : '#6b7280',
              }}
            >
              <span className="text-lg leading-none">{item.icon}</span>
              <span className="text-[10px] font-medium leading-none">{item.label}</span>
              {active && (
                <div className="absolute bottom-1 w-4 h-0.5 rounded-full" style={{ background: '#818cf8' }} />
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
};
